import client from "./client";
import type {
  LoginRequest,
  MfaEnrollConfirmResponse,
  MfaEnrollStart,
  TokenResponse,
  User,
} from "@/types/auth";
import type { LoginResponse } from "./twoFactor";

/**
 * /auth/login — returns either a TokenResponse on full success or a
 * LoginRequires2FA payload (``{ requires_2fa: true, temp_token, … }``)
 * when the user has 2FA enabled. Callers should use
 * ``isLoginRequires2FA`` from twoFactor.ts to discriminate.
 */
export async function login(data: LoginRequest): Promise<LoginResponse> {
  const res = await client.post<LoginResponse>("/auth/login", data);
  return res.data;
}

export type DemoPersona = "hr_admin" | "manager" | "employee" | "executive";

/** Mint a 30-min demo access token tied to a pre-seeded persona. Public,
 *  no password, no CSRF — the backend enforces a per-IP rate limit.
 *
 *  Deliberately uses the native ``fetch`` API instead of the shared axios
 *  client. The shared client's request interceptor tries to read the
 *  ``csrf_token`` cookie and attach an ``X-CSRF-Token`` header; in the
 *  production cross-origin setup (pages.dev → onrender.com) the cookie is
 *  set on the API origin and cannot be read from the SPA origin, so the
 *  header is never attached anyway — but by side-stepping the client
 *  entirely we also avoid the 401-retry / bootToLogin interceptor chain,
 *  which can race the demo mint when an expired token is still in
 *  localStorage from a previous session. */
export async function demoLogin(persona: DemoPersona): Promise<TokenResponse> {
  const { API_BASE_URL } = await import("@/lib/constants");
  const res = await fetch(`${API_BASE_URL}/auth/demo-login`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    credentials: "include",
    body: JSON.stringify({ persona }),
  });
  if (!res.ok) {
    const text = await res.text();
    let detail: unknown = text;
    try {
      detail = JSON.parse(text)?.detail ?? text;
    } catch {
      /* plain text */
    }
    const msg =
      typeof detail === "string"
        ? detail
        : "Couldn't start the demo — please try again.";
    throw new Error(msg);
  }
  return (await res.json()) as TokenResponse;
}

export async function refreshToken(): Promise<{ access_token: string }> {
  // Cookie-based; body is ignored when refresh_token cookie is present
  const res = await client.post("/auth/refresh", {});
  return res.data;
}

export async function logout(): Promise<void> {
  await client.post("/auth/logout");
}

export async function getMe(): Promise<User> {
  const res = await client.get<User>("/auth/me");
  return res.data;
}

export async function changePassword(
  currentPassword: string,
  newPassword: string
): Promise<void> {
  await client.post("/auth/change-password", {
    current_password: currentPassword,
    new_password: newPassword,
  });
}

// -------- Password reset --------

export async function requestPasswordReset(
  email: string
): Promise<{ message: string; dev_reset_token?: string }> {
  const res = await client.post("/auth/password-reset/request", { email });
  return res.data;
}

export async function confirmPasswordReset(
  token: string,
  newPassword: string
): Promise<void> {
  await client.post("/auth/password-reset/confirm", {
    token,
    new_password: newPassword,
  });
}

// -------- MFA --------

export async function startMfaEnrollment(): Promise<MfaEnrollStart> {
  const res = await client.post<MfaEnrollStart>("/auth/mfa/enroll/start");
  return res.data;
}

export async function confirmMfaEnrollment(
  code: string
): Promise<MfaEnrollConfirmResponse> {
  const res = await client.post<MfaEnrollConfirmResponse>(
    "/auth/mfa/enroll/confirm",
    { code }
  );
  return res.data;
}

export async function disableMfa(password: string): Promise<void> {
  await client.post("/auth/mfa/disable", { password });
}

// -------- CSRF --------

/**
 * Ask the backend to set the ``csrf_token`` cookie. Called on app bootstrap
 * so state-changing requests (including the very first login) can attach
 * the ``X-CSRF-Token`` header.
 */
export async function fetchCsrfToken(): Promise<string> {
  const res = await client.get<{ csrf_token: string }>("/auth/csrf");
  const token = res.data.csrf_token;
  // Mirror the result into the in-memory CSRF store on ``client`` so
  // the axios interceptor + streamCommand can use it. Cross-origin
  // deploys can't read the cookie via ``document.cookie``, so this
  // in-memory copy is the only place the SPA has the token.
  const { setCsrfTokenInMemory } = await import("./client");
  setCsrfTokenInMemory(token);
  return token;
}

// -------- Sessions --------

export interface SessionInfo {
  id: string;
  created_at: string;
  expires_at: string;
  user_agent: string | null;
  ip_address: string | null;
  is_current: boolean;
}

export async function listSessions(): Promise<SessionInfo[]> {
  const res = await client.get<{ sessions: SessionInfo[] }>("/auth/sessions");
  return res.data.sessions;
}

export async function revokeSession(sessionId: string): Promise<void> {
  await client.post(`/auth/sessions/${sessionId}/revoke`);
}

export async function revokeAllOtherSessions(): Promise<number> {
  const res = await client.post<{ revoked: number }>("/auth/sessions/revoke-all");
  return res.data.revoked;
}
